// ==============================
// File: src/UploadToken.jsx
// ==============================
import React, { useState, useCallback } from "react";
import { Upload, Trash2, FileJson, Loader2 } from "lucide-react";
import { uploadAdminToken } from "../api/Landlords";

function formatApiError(err) {
  const raw =
    err?.message ||
    (typeof err === "object" ? JSON.stringify(err) : String(err)) ||
    "Unknown error";

  // Network / CORS / offline
  if (/Failed to fetch|NetworkError|Load failed/i.test(raw)) {
    return "Network error. Check connection or CORS.";
  }

  try {
    const j = JSON.parse(raw);
    // Shape: { error: { error, detail, code }, detail, code }
    const nested = j?.error && typeof j.error === "object" ? j.error : null;
    const title =
      nested?.error ||
      nested?.message ||
      j?.message ||
      j?.detail ||
      "Request failed";
    const detail = nested?.detail || j?.detail || "";
    const code = nested?.code || j?.code || "";
    return [title, detail, code].filter(Boolean).join(" — ");
  } catch {
    // Not JSON -> just show string
    return raw;
  }
}

export default function UploadToken() {
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(false);

  const resetFeedbacks = () => {
    setSaved(false);
    setError("");
  };

  const isJsonFile = (f) => {
    const nameOk = /\.json$/i.test(f?.name || "");
    const typeOk = /json/i.test(f?.type || "");
    return nameOk || typeOk;
  };

  const handleFile = async (f) => {
    resetFeedbacks();
    if (!f) return;
    if (!isJsonFile(f)) {
      setError("Only .json files are allowed.");
      return;
    }
    if (f.size > 1024 * 1024) {
      setError("File must be ≤ 1MB.");
      return;
    }
    setFile(f);
    setFileName(f.name || "token.json");
  };

  const chooseFile = useCallback(() => {
    if (loading) return; // prevent changing while uploading
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "application/json,.json";
    input.onchange = (e) => handleFile(e.target.files?.[0]);
    input.click();
  }, [loading]);

  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (loading) return; // don't allow during upload
    setDragActive(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (loading) return;
    setDragActive(false);
  };
  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (loading) return;
    setDragActive(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  const clearFile = () => {
    if (loading) return;
    setFile(null);
    setFileName("");
    setError("");
    setSaved(false);
  };

  const save = async () => {
    setSaved(false);
    setError("");

    if (!file) {
      setError("Please select a token.json file first.");
      return;
    }
    if (loading) return; // guard double submit

    setLoading(true);
    try {
      await uploadAdminToken(file);
      setSaved(true);

      // Clear selection so “Selected: token.json” disappears after success.
      setFile(null);
      setFileName("");
      setDragActive(false);

      // Keep success visible briefly; harmless in SPA
      setTimeout(() => setSaved(false), 1500);
    } catch (e) {
      setError(formatApiError(e)); // e.g., "Token upload failed — Invalid token structure — UPLOAD_FAILED"
      // Keep current file so user can adjust/retry
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="flex items-center justify-center lg:justify-between">
          <h1 className="text-xl sm:text-2xl font-semibold">Upload Token</h1>
        </div>

        {/* Dropzone */}
        <div
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          aria-busy={loading ? "true" : "false"}
          className={`rounded-2xl border-2 border-dashed p-6 sm:p-8 bg-white/80 backdrop-blur shadow-sm transition-all ${
            dragActive ? "border-blue-500 bg-blue-50/50" : "border-gray-200"
          } ${loading ? "pointer-events-none opacity-70" : ""}`}
        >
          <div className="flex flex-col items-center text-center gap-3">
            <div className="h-12 w-12 rounded-2xl grid place-items-center bg-gradient-to-br from-blue-600 to-blue-300 text-white shadow">
              <FileJson className="h-6 w-6" />
            </div>
            <div className="text-sm text-gray-600">
              Drag & drop a <span className="font-medium">token.json</span>{" "}
              here, or{" "}
              <button
                onClick={chooseFile}
                disabled={loading}
                className="text-blue-600 underline disabled:opacity-50"
              >
                browse
              </button>
            </div>
            <div className="text-[11px] text-gray-500">Only JSON · Max 1MB</div>

            {fileName && (
              <div className="mt-2 text-xs text-gray-600">
                Selected: <span className="font-medium">{fileName}</span>
              </div>
            )}

            <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
              <button
                onClick={chooseFile}
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-50"
              >
                <Upload className="h-4 w-4" /> Choose .json
              </button>
              <button
                onClick={clearFile}
                disabled={(!file && !fileName) || loading}
                className="inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-50 text-red-600"
              >
                <Trash2 className="h-4 w-4" /> Clear
              </button>
            </div>
          </div>
        </div>

        {/* Result / actions */}
        <div className="rounded-2xl border bg-white/80 backdrop-blur shadow-sm p-6 space-y-4">
          <div aria-live="polite" className="space-y-3">
            {error && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2 whitespace-pre-wrap">
                {error}
              </div>
            )}
            {saved && (
              <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-xl px-3 py-2">
                Token uploaded successfully. Previous token was replaced.
              </div>
            )}
          </div>

          <div className="flex justify-end">
            <button
              onClick={save}
              disabled={!file || loading}
              className="inline-flex items-center gap-2 rounded-xl px-5 py-2 bg-gradient-to-r from-blue-600 to-violet-600 text-white shadow hover:shadow-md disabled:opacity-50"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Uploading…
                </>
              ) : (
                <>Save Token</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
